import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import { setPreference, usePreference } from '@fs/zion-preferences'

const DARK_MODE_FEATURE_FLAG = 'rip_sharedImageViewer_darkMode'
const DARK_MODE_PREFERENCE_KEY = 'shared-image-viewer.darkMode'

const DEFAULT_DARK_MODE_CONTEXT = {
  isDarkModeOn: false,
  loading: false,
  setIsDarkModeOn: () => {},
  theme: 'day',
  useAllowDarkMode: () => {},
}

const DarkModeContext = createContext(DEFAULT_DARK_MODE_CONTEXT)

export default function DarkModeProvider({ children }) {
  const { isOn: isDarkModeFeatureEnabled } = useFeatureFlag(DARK_MODE_FEATURE_FLAG)
  const [isDarkModeOn, setIsDarkModeOn] = useState()
  const [isDarkModeAllowedOnPage, setIsDarkModeAllowedOnPage] = useState(false)
  const initialDarkModePreference = usePreference(DARK_MODE_PREFERENCE_KEY)

  useEffect(() => {
    if (typeof initialDarkModePreference === 'boolean') {
      setIsDarkModeOn(initialDarkModePreference)
    } else {
      setIsDarkModeOn(false)
    }
  }, [initialDarkModePreference])

  const setDarkMode = useCallback(
    async (isOn) => {
      setIsDarkModeOn(isOn)
      if (!isDarkModeFeatureEnabled) return

      try {
        await setPreference(DARK_MODE_PREFERENCE_KEY, isOn)
      } catch (error) {
        console.error('Failed to persist dark mode preference', error)
      }
    },
    [isDarkModeFeatureEnabled]
  )

  const theme = isDarkModeFeatureEnabled && isDarkModeOn && isDarkModeAllowedOnPage ? 'night' : 'day'

  const cleanupAllowDarkMode = useCallback(() => {
    setIsDarkModeAllowedOnPage(false)
  }, [])

  const handleAllowDarkModeEffect = useCallback(() => {
    setIsDarkModeAllowedOnPage(true)
    return cleanupAllowDarkMode
  }, [cleanupAllowDarkMode])

  const useAllowDarkMode = useMemo(() => {
    if (!isDarkModeFeatureEnabled) {
      return DEFAULT_DARK_MODE_CONTEXT.useAllowDarkMode
    }
    return function useAllowDarkModeHook() {
      useEffect(handleAllowDarkModeEffect, [])
    }
  }, [isDarkModeFeatureEnabled, handleAllowDarkModeEffect])

  const value = useMemo(
    () => ({
      isDarkModeOn: isDarkModeFeatureEnabled ? isDarkModeOn : false,
      loading: isDarkModeFeatureEnabled ? isDarkModeOn === undefined : false,
      setIsDarkModeOn: isDarkModeFeatureEnabled ? setDarkMode : DEFAULT_DARK_MODE_CONTEXT.setIsDarkModeOn,
      theme: isDarkModeFeatureEnabled ? theme : DEFAULT_DARK_MODE_CONTEXT.theme,
      useAllowDarkMode: isDarkModeFeatureEnabled ? useAllowDarkMode : DEFAULT_DARK_MODE_CONTEXT.useAllowDarkMode,
    }),
    [isDarkModeFeatureEnabled, isDarkModeOn, setDarkMode, theme, useAllowDarkMode]
  )

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>
}

export { DarkModeContext }
